import { useCallback, useRef, useState } from "react";
import { Edge, Node } from "reactflow";

export interface HistoryState {
  nodes: Node[];
  edges: Edge[];
  workflowName?: string;
  timestamp: number;
}

export interface UseUndoRedoReturn {
  /** Whether undo is available */
  canUndo: boolean;
  /** Whether redo is available */
  canRedo: boolean;
  /** Undo the last action */
  undo: () => HistoryState | null;
  /** Redo the last undone action */
  redo: () => HistoryState | null;
  /** Take a snapshot of the current state to enable undo */
  takeSnapshot: (nodes: Node[], edges: Edge[], workflowName?: string) => void;
  /** Save current state to future stack (called by WorkflowBuilder during undo) */
  pushToFuture: (nodes: Node[], edges: Edge[], workflowName?: string) => void;
  /** Save current state to past stack (called by WorkflowBuilder during redo) */
  pushToPast: (nodes: Node[], edges: Edge[], workflowName?: string) => void;
  /** Clear all history */
  clearHistory: () => void;
  /** Get current history length (for debugging) */
  historyLength: number;
  /** Get current future length (for debugging) */
  futureLength: number;
}

const MAX_HISTORY_SIZE = 100;

/**
 * Custom hook for undo/redo functionality in the workflow builder.
 * Maintains a history stack of node/edge states and allows navigating back and forth.
 *
 * Usage:
 * 1. Call takeSnapshot() BEFORE making any change to capture the "before" state
 * 2. The undo() function will restore to the previous snapshot
 * 3. The redo() function will restore to a state that was undone
 *
 * Note: Snapshots are compared by stringified content to avoid duplicate entries.
 */
export function useUndoRedo(): UseUndoRedoReturn {
  // History stack (past states)
  const [past, setPast] = useState<HistoryState[]>([]);
  // Future stack (undone states for redo)
  const [future, setFuture] = useState<HistoryState[]>([]);

  // Track the current state to avoid duplicate snapshots
  const lastSnapshotRef = useRef<string>("");

  /**
   * Take a snapshot of the current state before making changes.
   * This should be called BEFORE the change is made so we can undo back to this state.
   */
  const takeSnapshot = useCallback((nodes: Node[], edges: Edge[], workflowName?: string) => {
    // Create a serializable snapshot (strip functions and circular refs)
    const snapshot: HistoryState = {
      nodes: nodes.map((node) => ({
        ...node,
        // Deep clone data to preserve config at this point in time
        data: JSON.parse(JSON.stringify(node.data || {}))
      })),
      edges: edges.map((edge) => ({ ...edge })),
      workflowName,
      timestamp: Date.now()
    };

    // Create a hash of the state to detect duplicates
    const stateHash = JSON.stringify({
      nodes: snapshot.nodes,
      edges: snapshot.edges,
      workflowName
    });

    // Don't add duplicate snapshots
    if (stateHash === lastSnapshotRef.current) {
      return;
    }

    lastSnapshotRef.current = stateHash;

    setPast((prevPast) => {
      const newPast = [...prevPast, snapshot];
      // Limit history size to prevent memory issues
      if (newPast.length > MAX_HISTORY_SIZE) {
        return newPast.slice(-MAX_HISTORY_SIZE);
      }
      return newPast;
    });

    // Clear future when a new action is taken (standard undo/redo behavior)
    setFuture([]);
  }, []);

  /**
   * Undo the last action by restoring the previous state.
   * Returns the state to restore, or null if nothing to undo.
   */
  const undo = useCallback((): HistoryState | null => {
    if (past.length === 0) {
      return null;
    }

    // Get the last state from history
    const previousState = past[past.length - 1];

    // We need the CURRENT state to push to future
    // This will be passed by the caller after calling undo

    setPast((prevPast) => prevPast.slice(0, -1));

    // Update the last snapshot ref to the restored state
    lastSnapshotRef.current = JSON.stringify({
      nodes: previousState.nodes,
      edges: previousState.edges,
      workflowName: previousState.workflowName
    });

    return previousState;
  }, [past]);

  /**
   * Save current state to future stack (called by WorkflowBuilder during undo)
   */
  const pushToFuture = useCallback((nodes: Node[], edges: Edge[], workflowName?: string) => {
    const currentState: HistoryState = {
      nodes: nodes.map((node) => ({
        ...node,
        data: JSON.parse(JSON.stringify(node.data || {}))
      })),
      edges: edges.map((edge) => ({ ...edge })),
      workflowName,
      timestamp: Date.now()
    };

    setFuture((prevFuture) => [...prevFuture, currentState]);
  }, []);

  /**
   * Redo the last undone action.
   * Returns the state to restore, or null if nothing to redo.
   */
  const redo = useCallback((): HistoryState | null => {
    if (future.length === 0) {
      return null;
    }

    // Get the next state from future
    const nextState = future[future.length - 1];

    setFuture((prevFuture) => prevFuture.slice(0, -1));

    // Update the last snapshot ref to the restored state
    lastSnapshotRef.current = JSON.stringify({
      nodes: nextState.nodes,
      edges: nextState.edges,
      workflowName: nextState.workflowName
    });

    return nextState;
  }, [future]);

  /**
   * Save current state to past stack (called by WorkflowBuilder during redo)
   */
  const pushToPast = useCallback((nodes: Node[], edges: Edge[], workflowName?: string) => {
    const currentState: HistoryState = {
      nodes: nodes.map((node) => ({
        ...node,
        data: JSON.parse(JSON.stringify(node.data || {}))
      })),
      edges: edges.map((edge) => ({ ...edge })),
      workflowName,
      timestamp: Date.now()
    };

    setPast((prevPast) => {
      const newPast = [...prevPast, currentState];
      if (newPast.length > MAX_HISTORY_SIZE) {
        return newPast.slice(-MAX_HISTORY_SIZE);
      }
      return newPast;
    });
  }, []);

  /**
   * Clear all history (useful when loading a new workflow)
   */
  const clearHistory = useCallback(() => {
    setPast([]);
    setFuture([]);
    lastSnapshotRef.current = "";
  }, []);

  return {
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    undo,
    redo,
    takeSnapshot,
    pushToFuture,
    pushToPast,
    clearHistory,
    historyLength: past.length,
    futureLength: future.length
  };
}
